import React, { useState, useEffect, useRef, ReactNode } from 'react';
import CoachMark from '@/components/common/CoachMark';
import SafeViewportOverlay from '@/components/common/SafeViewportOverlay';
import { useCoachMark } from '@/hooks/useCoachMark';
import { PlayerRead, PlayerSeasonSummary, PlayerGameLogResponse, PlayerGameLogEntry, PlayerGameLogEntryStats, ResearchSeasonOption } from '@/api/schemas';
import { fetchPlayerActiveSeasonGameLog, fetchPlayerSeasonSummary, fetchResearchSeasons } from '@/api/stats';
import { fetchPlayerSeasonRank } from '@/api/stats';
import { useQuery } from '@tanstack/react-query';
import { formatPositionRank, normalizedRankCompetitionIds } from '@/utils/playerRank';
import { userFacingError } from '@/utils/userFacingError';
import { seasonPillLabel, sortSeasonsChronologically } from '@/utils/seasons';
import { fetchPlayerProjections, PlayerProjectionDetail } from '@/api/dfs';
import { fetchPlayerLeagueHistory } from '@/api/activity';
import { trackPlayerResearch } from '@/utils/research';
import { useAuth } from '@/contexts/AuthContext';
import Spinner from '@/components/common/Spinner';
import AvailabilityBadge from '@/components/common/AvailabilityBadge';
import InjuryBadge from '@/components/common/InjuryBadge';
import { useConfirmDialog } from '@/components/common/ConfirmDialog';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { useDialogFocus } from '@/hooks/useDialogFocus';
import { useAppBackButtonInterceptor } from '@/hooks/useAppBackButtonInterceptor';
import SeasonScoreSummary from '@/components/common/SeasonScoreSummary';
import { CLUB_SCORING_METRICS } from '@/utils/clubResearch';
import { useExitTransition } from '@/hooks/useExitTransition';
import { formatPlayerDisplayName } from '@/utils/formatting';
import { getAbbreviation } from '@/utils/teams';
import { OPRK_TOOLTIP } from '@/utils/playerDisplay';
import { format, parseISO } from 'date-fns';
import { Target, Zap, KeyRound, Handshake, MoveRight, Footprints, XCircle, Icon } from 'lucide-react';
import { soccerBall } from '@lucide/lab';

const logger = console;

// --- REVISED: Updated STAT_ABBREVIATIONS constant ---
// Keys now match the backend data model fields (e.g., 'prog_passes')
// Values are the new, user-friendly abbreviations.
const STAT_ABBREVIATIONS: Record<string, string> = {
  goals: "G",
  shots_on_target: "SOT",
  shots: "SH",
  gca: "GC", // Changed from GCA
  sca: "SC", // Changed from SCA
  prog_passes: "IP", // Changed from PP
  prog_carries: "IC", // Changed from PC
  dispossessed: "DIS",
};
// --- END REVISION ---


interface PlayerProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  player: PlayerRead | null;
  competitionId: number | null;
  competitionName?: string | null;
  isMyPlayer?: boolean;
  onDropPlayer?: (player: PlayerRead) => void;
  dropActionLabel?: string;
  dropConfirmationMessage?: string;
  onAddPlayer?: (player: PlayerRead, actionType: 'WAIVER' | 'FCFS') => void;
  onTradePlayer?: (player: PlayerRead) => void;
  isActionLoading?: boolean;
  dfsContestId?: number | null;
  portalContainer?: Element | null;
  /** Render above another open modal, so closing the profile returns to it. */
  isStacked?: boolean;
  /** Enable dialog focus/back handling for a profile nested in club research. */
  manageFocus?: boolean;
  /** Honor competitionId exactly - skip the server's per-player competition
      inference. Historical/stats pages set this so a transferred player
      doesn't jump to his current league's seasons. */
  pinCompetition?: boolean;
  /** League/contest/page competition scope for current-season ranks. */
  rankCompetitionIds?: number[];
  /** Classic mode uses its own scoring rules and does not show these ranks. */
  showSeasonRanks?: boolean;
  /** League pages only: enables the Activity tab (the player's roster history
      in this league). */
  leagueId?: number | null;
}

type ModalTabKey = 'gameLog' | 'seasonStats' | 'projections' | 'activity';

// --- REVISED: Updated STAT_ICONS keys ---
// Keys now match the backend data model fields.
const STAT_ICONS: Record<string, ReactNode> = {
  goals: <Icon iconNode={soccerBall} size={14} className="text-accent-yellow" />,
  shots_on_target: <Target size={14} className="text-accent-yellow" />,
  shots: <Zap size={14} className="text-accent-yellow" />,
  gca: <KeyRound size={14} className="text-accent-yellow" />,
  sca: <Handshake size={14} className="text-accent-yellow" />,
  prog_passes: <MoveRight size={14} className="text-accent-yellow" />,
  prog_carries: <Footprints size={14} className="text-accent-yellow" />,
  dispossessed: <XCircle size={14} className="text-red-400" />,
};
// --- END REVISION ---

// Projection stat keys mapped to display abbreviations and icons
const PROJECTION_STAT_ORDER: { key: string; label: string }[] = [
  { key: 'Goal', label: 'G' },
  { key: 'Shot', label: 'SH' },
  { key: 'ShotOnTarget', label: 'SOT' },
  { key: 'SCA', label: 'SC' },
  { key: 'GCA', label: 'GC' },
  { key: 'ProgPass', label: 'IP' },
  { key: 'ProgCarry', label: 'IC' },
  { key: 'Dispossessed', label: 'DIS' },
];

const PROJECTION_STAT_ICONS: Record<string, ReactNode> = {
  Goal: <Icon iconNode={soccerBall} size={14} className="text-accent-yellow" />, Shot: <Zap size={14} className="text-accent-yellow" />, ShotOnTarget: <Target size={14} className="text-accent-yellow" />, SCA: <Handshake size={14} className="text-accent-yellow" />, GCA: <KeyRound size={14} className="text-accent-yellow" />,
  ProgPass: <MoveRight size={14} className="text-category-progressing" />, ProgCarry: <Footprints size={14} className="text-category-progressing" />, Dispossessed: <XCircle size={14} className="text-red-400" />,
};

// Point values keyed by projection stat key (mirrors backend projection_service logic)
const PROJECTION_POINT_VALUES: Record<string, number> = {
  Goal: 8, Shot: 1, ShotOnTarget: 3, SCA: 1.5, GCA: 6,
  ProgPass: 1, ProgCarry: 1, Dispossessed: -1,
};

// Full stat names for definitions view (mobile-friendly — no abbreviations)
const STAT_FULL_NAMES: Record<string, string> = {
  goals: 'Goal',
  shots_on_target: 'Shot on Target',
  shots: 'Shot',
  gca: 'Goal Creation',
  sca: 'Shot Creation',
  prog_passes: 'Impact Pass',
  prog_carries: 'Impact Carry',
  dispossessed: 'Dispossessed',
};

const PROJECTION_STAT_FULL_NAMES: Record<string, string> = {
  Goal: 'Goal',
  Shot: 'Shot',
  ShotOnTarget: 'Shot on Target',
  SCA: 'Shot Creation',
  GCA: 'Goal Creation',
  ProgPass: 'Impact Pass',
  ProgCarry: 'Impact Carry',
  Dispossessed: 'Dispossessed',
};

// Stat definitions for inline reference (Season Stats table uses backend field keys)
const STAT_DEFINITIONS: Record<string, string> = {
  goals: 'Goals',
  shots: 'Shots Attempted',
  shots_on_target: 'Shots on Target',
  gca: 'Offensive Action (Pass, Foul Won, Previous Shot) leading directly to a Goal',
  sca: 'Offensive Action (Pass, Foul Won, Previous Shot) leading directly to a Shot',
  prog_passes: 'Pass in/into the attacking half that critically advances the ball 10 yards closer to the opposing goal',
  prog_carries: 'Carry in/into the attacking half that critically advances the ball 10 yards closer to the opposing goal',
  dispossessed: 'Lost possession',
};

// Stat definitions for Projections table (uses projection stat keys)
const PROJECTION_STAT_DEFINITIONS: Record<string, string> = {
  Goal: 'Goals',
  Shot: 'Shots Attempted',
  ShotOnTarget: 'Shots on Target',
  GCA: 'Offensive Action (Pass, Foul Won, Previous Shot) leading directly to a Goal',
  SCA: 'Offensive Action (Pass, Foul Won, Previous Shot) leading directly to a Shot',
  ProgPass: 'Pass in/into the attacking half that critically advances the ball 10 yards closer to the opposing goal',
  ProgCarry: 'Carry in/into the attacking half that critically advances the ball 10 yards closer to the opposing goal',
  Dispossessed: 'Lost possession',
};

// Fixed display order for definitions view (matches scoring page category order)
const SEASON_STAT_DEFINITION_ORDER = ['goals', 'shots_on_target', 'shots', 'gca', 'sca', 'prog_passes', 'prog_carries', 'dispossessed'];

const fmtOrdinal = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Compact league tag for disambiguating same-year pills ("Premier League" ->
// "EPL", "La Liga" -> "La Liga"); falls back to the first word.
const getCompetitionShortLabel = (name: string | null | undefined): string => {
  if (!name) return '';
  const n = name.toLowerCase();
  if (n.includes('premier')) return 'EPL';
  if (n.includes('la liga')) return 'La Liga';
  return name.split(' ')[0];
};

const getRankColor = (rank: number | undefined): string => {
  if (rank === undefined) return 'text-gray-400';
  if (rank <= 6) return 'text-red-400';   // Toughest defenses
  if (rank <= 13) return 'text-yellow-400'; // Middle
  return 'text-green-400';                  // Softest defenses
};

const PlayerProfileModal: React.FC<PlayerProfileModalProps> = ({
  isOpen, onClose, player, competitionId, competitionName,
  isMyPlayer = false,
  onDropPlayer,
  dropActionLabel = 'Drop',
  dropConfirmationMessage = "He'll be removed from your roster and become subject to league waiver rules.",
  onAddPlayer,
  onTradePlayer,
  isActionLoading = false,
  dfsContestId,
  portalContainer,
  isStacked = false,
  manageFocus = false,
  pinCompetition = false,
  rankCompetitionIds,
  showSeasonRanks = true,
  leagueId = null,
  // NOTE: the modal always DEFAULTS to the most recent season regardless of
  // the season the caller was browsing (owner call 2026-07-17) - the old
  // initialStatsSeasonIdentifier prop was removed as dead contract.
}) => {
  const { token } = useAuth();
  const showConfirm = useConfirmDialog();
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus(dialogRef, isOpen && manageFocus, onClose);
  useAppBackButtonInterceptor(isOpen && manageFocus, onClose);
  useBodyScrollLock(isOpen);
  const { render, closing } = useExitTransition(isOpen);
  const [activeTab, setActiveTab] = useState<ModalTabKey>('gameLog');

  // Activity tab (league pages only): the player's completed roster moves in
  // this league, newest first. Fetched only once the tab opens. Read-only, so
  // viewing it never clears the league Activity badge.
  const showLeagueActivity = leagueId != null && !dfsContestId;
  const {
    data: leagueHistory,
    error: leagueHistoryError,
    isPending: isLeagueHistoryPending,
    isFetching: isLeagueHistoryFetching,
    refetch: refetchLeagueHistory,
  } = useQuery({
    queryKey: ['player-league-history', leagueId, player?.id],
    queryFn: ({ signal }) => fetchPlayerLeagueHistory(leagueId!, player!.id, token!, signal),
    enabled: isOpen && showLeagueActivity && activeTab === 'activity' && !!player?.id && !!token,
    staleTime: 30_000,
    retry: 1,
  });
  useEffect(() => {
    if (isOpen && leagueHistoryError) {
      userFacingError(leagueHistoryError, null, {
        section: 'player-league-history', playerId: player?.id, leagueId,
      });
    }
  }, [isOpen, leagueHistoryError, player?.id, leagueId]);
  const [seasonStats, setSeasonStats] = useState<PlayerSeasonSummary | null>(null);
  const [gameLogData, setGameLogData] = useState<PlayerGameLogResponse | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState<boolean>(false);
  const [isLoadingGameLog, setIsLoadingGameLog] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [projectionData, setProjectionData] = useState<PlayerProjectionDetail | null>(null);
  const [isLoadingProjections, setIsLoadingProjections] = useState<boolean>(false);
  const [showSeasonDefinitions, setShowSeasonDefinitions] = useState(false);
  const [showProjectionDefinitions, setShowProjectionDefinitions] = useState(false);
  const [researchSeasons, setResearchSeasons] = useState<ResearchSeasonOption[]>([]);
  const [selectedStatsSeason, setSelectedStatsSeason] = useState<string | null>(null);
  // The player's OWN competition, resolved server-side from the seasons fetch.
  // Combined-league surfaces pass their anchor competition for every player
  // (a La Liga player opened from a combined draft board arrived with the EPL
  // comp id); all season/game-log fetches must use this once known.
  const [resolvedCompetitionId, setResolvedCompetitionId] = useState<number | null>(null);
  const [rankResolvedPlayerId, setRankResolvedPlayerId] = useState<number | null>(null);
  const [rankResolvedRequestCompetitionId, setRankResolvedRequestCompetitionId] = useState<number | null>(null);
  const explicitRankIds = normalizedRankCompetitionIds(rankCompetitionIds);
  const effectiveRankIds = rankCompetitionIds !== undefined
    ? explicitRankIds
    : rankResolvedPlayerId === player?.id && rankResolvedRequestCompetitionId === competitionId && resolvedCompetitionId
      ? [resolvedCompetitionId]
      : !competitionId && player?.research_competition_id
        ? [player.research_competition_id] : [];
  const rankScopeKey = effectiveRankIds.join(',');
  const { data: seasonRank, error: seasonRankError, isPending: isSeasonRankPending } = useQuery({
    queryKey: ['player-season-rank', player?.id, rankScopeKey],
    queryFn: ({ signal }) => fetchPlayerSeasonRank(player!.id, effectiveRankIds, signal),
    enabled: showSeasonRanks && isOpen && !!player?.id && effectiveRankIds.length > 0,
    staleTime: 30_000,
    retry: 1,
  });

  useEffect(() => {
    if (showSeasonRanks && isOpen && seasonRankError) {
      userFacingError(seasonRankError, null, {
        section: 'player-season-rank', playerId: player?.id, competitions: rankScopeKey,
      });
    }
  }, [showSeasonRanks, isOpen, seasonRankError, player?.id, rankScopeKey]);

  // First-run coach mark pointing at the research tabs. Shows once per device,
  // while the modal is open on its default Game Log tab.
  const tabsRef = useRef<HTMLDivElement>(null);
  const { show: showTabsCoach, dismiss: dismissTabsCoach } = useCoachMark(
    'profile_research_tabs',
    isOpen && activeTab === 'gameLog'
  );

  useEffect(() => {
    if (isOpen && player && player.id && competitionId) {
      // Conviction signal (STRATEGY C-ENG2): every profile open is a research action.
      trackPlayerResearch('profile', {
        'Player ID': player.id,
        'Competition ID': competitionId,
        'DFS Contest ID': dfsContestId ?? null,
      });
      setSeasonStats(null); setGameLogData(null); setProjectionData(null); setError(null); setActiveTab('gameLog');
      setResearchSeasons([]); setSelectedStatsSeason(null); setResolvedCompetitionId(null);
      setRankResolvedPlayerId(null); setRankResolvedRequestCompetitionId(null);
      setShowSeasonDefinitions(false); setShowProjectionDefinitions(false);
      const fetchData = async () => {
        if (dfsContestId) setIsLoadingProjections(true);
        try {
          // Game log is fetched by the season-selection effect below, so it
          // always matches the selected season pill.
          const promises: Promise<any>[] = [
            fetchResearchSeasons(competitionId, player.id, pinCompetition),
          ];
          if (dfsContestId) {
            promises.push(fetchPlayerProjections(dfsContestId, token));
          }
          const results = await Promise.allSettled(promises);
          const [seasonsRes] = results;
          if (seasonsRes.status === 'fulfilled') {
            // ALL seasons the PLAYER has played, oldest -> newest (cross-
            // competition movers carry their old league's pills). Default =
            // the most recent season WITH DATA for this player (owner call
            // 2026-07-23, superseding 2026-07-17's always-most-recent):
            // preseason the current pill is empty, and opening on a blank
            // tab was the #1 modal complaint. Flips to current automatically
            // once he records a 26/27 appearance.
            const options = sortSeasonsChronologically(seasonsRes.value as ResearchSeasonOption[]);
            setResearchSeasons(options);
            // The list is oldest -> newest; options[0] can be an archived
            // competition for a transferred player. The server marks the
            // evidence-backed current competition as the default; unlike pool
            // membership, that remains correct while stale removals are held.
            setResolvedCompetitionId(
              options.find(option => option.is_default)?.competition_id
              ?? options.find(option => option.is_current)?.competition_id
              ?? options[options.length - 1]?.competition_id
              ?? null
            );
            setRankResolvedPlayerId(player.id);
            setRankResolvedRequestCompetitionId(competitionId);
            const withData = options.filter(option => option.has_data);
            // pinCompetition callers (historical/stats pages) keep the league
            // the user is browsing as the DEFAULT - his other league's pills
            // stay available, they just don't hijack the opening view.
            const pinnedWithData = pinCompetition
              ? withData.filter(option => option.competition_id === competitionId)
              : withData;
            const preferred = pinnedWithData.length > 0 ? pinnedWithData : withData;
            const defaultOption = withData.find(option => option.is_default)
              ?? (preferred.length > 0 ? preferred[preferred.length - 1] : null)
              ?? options.find(option => option.is_default)
              ?? options.find(option => option.is_current)
              ?? options[options.length - 1];
            setSelectedStatsSeason(defaultOption?.season_identifier ?? null);
          } else {
            logger.error("Error fetching research seasons:", seasonsRes.reason);
            setError(prev => prev ? `${prev}\nSeason Stats Error` : "Season Stats Error");
          }
          if (dfsContestId && results[1]) {
            const projRes = results[1];
            if (projRes.status === 'fulfilled') {
              const allProjections = projRes.value as PlayerProjectionDetail[];
              const playerProj = allProjections.find(p => p.player_id === player.id) ?? null;
              setProjectionData(playerProj);
            } else {
              logger.error("Error fetching projections:", projRes.reason);
            }
          }
        } catch (err) {
          logger.error("Error fetching player profile data:", err);
          setError("Failed to load some player data.");
        }
        finally {
          setIsLoadingProjections(false);
        }
      };
      fetchData();
    }
  }, [isOpen, player, competitionId, dfsContestId, token, pinCompetition]);

  // Season Stats AND Game Log both follow the season selector (owner call
  // 2026-07-16). Projections remain explicitly current-contest. For the
  // current season the game log is fetched WITHOUT an identifier so it keeps
  // its upcoming-projection row; archived seasons fetch by identifier.
  useEffect(() => {
    if (!isOpen || !player?.id || !competitionId || !selectedStatsSeason) return;
    // Never fetch with the caller's comp before resolution lands: the seasons
    // fetch sets BOTH selectedStatsSeason and resolvedCompetitionId, so a
    // NULL here means we're mid-flight, not single-comp.
    if (resolvedCompetitionId === null) return;
    // THE SEASON PILL OWNS ITS COMPETITION (owner 2026-07-23): a cross-league
    // mover's archived pills point at the league he played that season in
    // (Cucurella's 2025/26 pill is an EPL row even though he's in the La Liga
    // pool today), so every fetch uses the SELECTED option's competition.
    const selectedOption = researchSeasons.find(option => option.season_identifier === selectedStatsSeason);
    const effectiveCompetitionId = selectedOption?.competition_id ?? resolvedCompetitionId;
    let cancelled = false;
    setIsLoadingStats(true);
    setSeasonStats(null);
    fetchPlayerSeasonSummary(player.id, effectiveCompetitionId, selectedStatsSeason, token)
      .then(summary => { if (!cancelled) setSeasonStats(summary); })
      .catch(err => {
        logger.error("Error fetching season stats:", err);
        if (!cancelled) setError(prev => prev ? `${prev}\nSeason Stats Error` : "Season Stats Error");
      })
      .finally(() => { if (!cancelled) setIsLoadingStats(false); });

    const logSeasonIdentifier = selectedOption && !selectedOption.is_current ? selectedStatsSeason : null;
    setIsLoadingGameLog(true);
    setGameLogData(null);
    fetchPlayerActiveSeasonGameLog(player.id, effectiveCompetitionId, token, logSeasonIdentifier)
      .then(log => { if (!cancelled) setGameLogData(log); })
      .catch(err => {
        logger.error("Error fetching game log:", err);
        if (!cancelled) setError(prev => prev ? `${prev}\nGame Log Error` : "Game Log Error");
      })
      .finally(() => { if (!cancelled) setIsLoadingGameLog(false); });

    return () => { cancelled = true; };
  }, [isOpen, player?.id, competitionId, resolvedCompetitionId, selectedStatsSeason, token, researchSeasons]);

  const statDisplayOrder: (keyof PlayerGameLogEntryStats)[] = ['fantasy_points', 'goals', 'shots', 'shots_on_target', 'sca', 'gca', 'prog_passes', 'prog_carries', 'dispossessed'];
  const statLabels: Record<keyof PlayerGameLogEntryStats, string> = {
    fantasy_points: "FPTS",
    goals: "G",
    shots: "SH",
    shots_on_target: "SOT",
    sca: "SC", // Updated abbreviation
    gca: "GC", // Updated abbreviation
    prog_passes: "IP", // Updated abbreviation
    prog_carries: "IC", // Updated abbreviation
    dispossessed: "DIS"
  };

  const formatStatValue = (value: number | null | undefined, key: keyof PlayerGameLogEntryStats, isUpcoming?: boolean): string => {
    if (value === null || value === undefined) return '-';
    if (key === 'fantasy_points') return value.toFixed(1);
    if (isUpcoming) return value.toFixed(1);
    return Number.isInteger(value) ? value.toString() : value.toFixed(1);
  };

  const handleDropClick = async () => {
    if (onDropPlayer && player) {
      const playerName = formatPlayerDisplayName(player);
      if (await showConfirm({
        title: `${dropActionLabel} ${playerName}?`,
        message: dropConfirmationMessage,
        confirmLabel: dropActionLabel,
        destructive: true,
      })) {
        onDropPlayer(player);
      }
    }
  };

  const handleAddClick = (actionType: 'WAIVER' | 'FCFS') => {
    if (onAddPlayer && player) {
      onAddPlayer(player, actionType);
    }
  };

  const handleTradeClick = () => {
    if (onTradePlayer && player) {
      onTradePlayer(player);
    }
  };

  if (!render || !player) return null;

  const displayName = formatPlayerDisplayName(player);
  const teamAbbr = getAbbreviation(player.current_team_name);
  // Historical list rows can carry an old position. The live rank is
  // partitioned by the player's current DB position, so keep the header and
  // rank label aligned to the server's current-season identity.
  const profilePosition = seasonRank ? seasonRank.position : player.primary_position;
  const selectedStatsSeasonOption = researchSeasons.find(
    option => option.season_identifier === selectedStatsSeason
  );
  // The header names the league of the SELECTED season - viewing a
  // cross-league mover's old season must not still say "La Liga". When the
  // selected season IS the caller's own competition, keep the caller's label:
  // some surfaces (DFS builder) deliberately pass a SHORT name ("EPL") that
  // the API's full name would otherwise overwrite.
  const displayCompetitionName =
    (selectedStatsSeasonOption && selectedStatsSeasonOption.competition_id !== competitionId
      ? selectedStatsSeasonOption.competition_name
      : null)
    || competitionName
    || selectedStatsSeasonOption?.competition_name
    || (seasonStats?.competition?.name ? `${seasonStats.competition.name}` : '');
  // Same year in two leagues (January cross-league mover) would render two
  // identical pills - disambiguate those with the competition name.
  const duplicateYearLabels = new Set(
    researchSeasons
      .map(o => seasonPillLabel(o.season_identifier))
      .filter((label, i, arr) => arr.indexOf(label) !== i)
  );

  const renderGameLogTab = () => (
    <div className="p-4">
      {gameLogData?.season_identifier && (
        <p className="text-badge text-gray-400 mb-3">
          Current game log · {gameLogData.season_identifier}
        </p>
      )}
      {isLoadingGameLog && (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <Spinner size="md" color="text-accent-yellow" />
            <p className="mt-2 text-sm text-gray-400">Loading game log...</p>
          </div>
        </div>
      )}

      {error && gameLogData === null && (
        <div className="text-center p-4">
          <p className="text-secondary text-sm">
            {error.includes("Game Log Error") || error.includes("Failed to load") ? error : "Error loading game log."}
          </p>
        </div>
      )}

      {gameLogData && gameLogData.game_log.length > 0 && !isLoadingGameLog && (() => {
        const startedCount = gameLogData.game_log.filter((g: PlayerGameLogEntry) => g.availability_status === 'STARTING').length;
        const benchCount = gameLogData.game_log.filter((g: PlayerGameLogEntry) => g.availability_status === 'BENCH').length;
        const outCount = gameLogData.game_log.filter((g: PlayerGameLogEntry) => g.availability_status === 'NOT_IN_SQUAD').length;
        const hasAvailabilityData = startedCount > 0 || benchCount > 0 || outCount > 0;

        return (<>
        {hasAvailabilityData && (
          <div className="bg-primary/30 rounded-lg p-3 mb-3 border border-gray-700/50">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-badge text-gray-500 uppercase tracking-wider">Started</p>
                <p className="text-lg font-bold text-green-400">{startedCount}</p>
              </div>
              <div>
                <p className="text-badge text-gray-500 uppercase tracking-wider">Bench</p>
                <p className="text-lg font-bold text-yellow-400">{benchCount}</p>
              </div>
              <div>
                <p className="text-badge text-gray-500 uppercase tracking-wider">Out</p>
                <p className="text-lg font-bold text-red-400">{outCount}</p>
              </div>
            </div>
          </div>
        )}
        {/* Keep the game log inside the modal's single two-axis scroller.
            A horizontal overflow container nested inside a vertical one can
            capture vertical swipes in iOS WebKit, which leaves the profile
            modal apparently frozen when the gesture begins on the table. */}
        <div className="-mx-4">
          <table className="w-max min-w-full text-left text-xs">
            <thead className="bg-primary/50 border-b border-gray-700">
              <tr className="text-gray-400">
                <th className="px-3 py-2.5 font-medium sticky left-0 bg-primary/50">Date</th>
                <th className="px-3 py-2.5 font-medium">Opp</th>
                {statDisplayOrder.map(key => (
                  <th key={String(key)} className={`px-3 py-2.5 text-right font-medium ${key === 'fantasy_points' ? 'text-accent-yellow' : ''}`}>
                    {statLabels[key]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30">
              {gameLogData.game_log.slice().reverse().map((log: PlayerGameLogEntry, index: number) => (
                <tr key={log.match_id} className={`${
                  log.is_upcoming
                    ? 'bg-info-blue/[0.08] border-l-2 border-info-blue'
                    : index % 2 === 0 ? 'bg-primary/20' : ''
                } hover:bg-primary/40 active:bg-primary/40 transition-colors`}>
                  <td className={`px-3 py-2.5 whitespace-nowrap sticky left-0 ${
                    log.is_upcoming
                      ? 'bg-info-blue/[0.08] text-gray-300'
                      : `text-gray-300 ${index % 2 === 0 ? 'bg-primary/20' : 'bg-surface-card'}`
                  }`}>
                    {format(parseISO(log.match_date), 'MMM d')}
                  </td>
                  <td className={`px-3 py-2.5 whitespace-nowrap text-gray-300`}>
                    {log.home_or_away}
                    {log.is_upcoming && <span className="text-micro text-info-blue opacity-70 ml-1">(Projected)</span>}
                    {!log.is_upcoming && log.availability_status && (
                      <span className="ml-1.5"><AvailabilityBadge status={log.availability_status} isLocked={true} /></span>
                    )}
                  </td>
                  {statDisplayOrder.map(key => (
                    <td
                      key={String(key)}
                      className={`px-3 py-2.5 text-right font-medium ${
                        log.is_upcoming
                          ? key === 'fantasy_points' ? 'text-accent-yellow italic' : 'text-gray-400 italic'
                          : key === 'fantasy_points' ? 'text-accent-yellow font-bold' : 'text-gray-200'
                      }`}
                    >
                      {formatStatValue(log.stats[key], key, log.is_upcoming)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>);
      })()}

      {gameLogData && gameLogData.game_log.length === 0 && !isLoadingGameLog && !error && (
        <p className="text-gray-500 italic text-center py-8 text-sm">
          No game log entries found for this season.
        </p>
      )}
    </div>
  );

  const renderSeasonStatsTab = () => (
    <>
      {isLoadingStats && (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <Spinner size="md" color="text-accent-yellow" />
            <p className="mt-2 text-sm text-gray-400">Loading season stats...</p>
          </div>
        </div>
      )}

      {error && seasonStats === null && (
        <div className="text-center p-4">
          <p className="text-secondary text-sm">
            {error.includes("Season Stats Error") || error.includes("Failed to load") ? error : "Error loading season stats."}
          </p>
        </div>
      )}

      {seasonStats && !isLoadingStats && (
        <div className="px-4 py-3">
          {/* Key Stats Summary */}
          <div className="bg-primary/30 rounded-lg p-4 mb-4 border border-gray-700/50">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-badge text-gray-500 uppercase tracking-wider">FPPG</p>
                <p className="text-xl font-bold text-accent-yellow">
                  {seasonStats.fppg?.toFixed(1) ?? '-'}
                </p>
              </div>
              <div>
                <p className="text-badge text-gray-500 uppercase tracking-wider">Total FPTS</p>
                <p className="text-xl font-bold text-gray-200">
                  {seasonStats.fantasy_points?.toFixed(1) ?? '-'}
                </p>
              </div>
              <div>
                <p className="text-badge text-gray-500 uppercase tracking-wider">Games</p>
                <p className="text-xl font-bold text-gray-200">
                  {seasonStats.player_games_appeared ?? seasonStats.games_played ?? '-'}
                </p>
              </div>
            </div>
            {/* Set-piece duty chips: rank-verbiage + share for magnitude -
                "Led team in free kicks (74%)" answers "is this good?" where
                a bare % couldn't (owner iteration 2026-07-20). Only team
                ranks 1-2 chip (pens: leader only); absent = no real role. */}
            {(seasonStats.set_piece_duty?.length ?? 0) > 0 && (
              <div className="flex flex-wrap justify-center gap-1.5 mt-3 pt-3 border-t border-gray-700/50">
                {seasonStats.set_piece_duty!.map((d, i) => {
                  const what = d.duty === 'corner' ? 'corners' : d.duty === 'penalty' ? 'pens' : 'free kicks';
                  const whatLong = d.duty === 'corner' ? 'corners' : d.duty === 'penalty' ? 'penalties' : 'attacking free kicks';
                  // team_rank can be absent for one deploy-skew window (old
                  // backend response + new frontend) - fall back to the
                  // neutral label rather than implying a rank we don't have.
                  const verb = d.team_rank == null
                    ? `On ${what}`
                    : d.team_rank === 1
                      ? (d.co_led ? `Co-led team in ${what}` : `Led team in ${what}`)
                      : `2nd on team in ${what}`;
                  return (
                    <span
                      key={i}
                      className="px-2 py-0.5 text-badge font-bold rounded-full bg-accent-yellow/15 text-accent-yellow ring-1 ring-accent-yellow/30"
                      title={`Took ${Math.round(d.share * 100)}% of ${(d.team_name ?? 'the team') + "'s"} ${whatLong} this season (${d.count} taken)`}
                    >
                      {verb} ({Math.round(d.share * 100)}%)
                    </span>
                  );
                })}
              </div>
            )}
          </div>

          {/* Season Statistics Table */}
          <div className="bg-primary/20 rounded-lg p-4 border border-gray-700/30">
            {!showSeasonDefinitions ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Season Statistics</h3>
                  <button
                    onClick={() => setShowSeasonDefinitions(true)}
                    className="text-badge text-accent-light-blue hover:text-accent-yellow active:text-accent-yellow transition-colors flex items-center gap-1"
                  >
                    Definitions →
                  </button>
                </div>
                <SeasonScoreSummary compact counts={Object.fromEntries(CLUB_SCORING_METRICS.map(metric => [metric.key, seasonStats[metric.field] ?? 0]))} />
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowSeasonDefinitions(false)}
                  className="text-badge text-accent-light-blue hover:text-accent-yellow transition-colors mb-2 flex items-center gap-1"
                >
                  ← Back to stats
                </button>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 font-medium">
                      <th className="text-left py-1 pr-2 w-[72px]">Type</th>
                      <th className="text-left py-1 pl-2">Definition</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SEASON_STAT_DEFINITION_ORDER.map(key => (
                      <tr key={key} className="border-t border-gray-700/30">
                        <td className="py-1.5 pr-2">
                          <div className="flex items-center space-x-1.5">
                            <span className="text-sm">{STAT_ICONS[key] || '•'}</span>
                            <span className="font-semibold text-gray-300">
                              {STAT_FULL_NAMES[key] || STAT_ABBREVIATIONS[key]}
                            </span>
                          </div>
                        </td>
                        <td className="py-1.5 pl-2 text-gray-400">
                          {STAT_DEFINITIONS[key]}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}

      {!seasonStats && !isLoadingStats && !error && (
        <p className="text-gray-500 italic text-center py-8 text-sm">
          No {displayCompetitionName || 'competition'} statistics for this player
          {selectedStatsSeasonOption ? ` in ${selectedStatsSeasonOption.season_identifier}` : ''}.
        </p>
      )}
    </>
  );

  const renderActivityTab = () => {
    if (isLeagueHistoryPending || isLeagueHistoryFetching) {
      return (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <Spinner size="md" color="text-accent-yellow" />
            <p className="mt-2 text-sm text-gray-400">Loading league history...</p>
          </div>
        </div>
      );
    }
    if (leagueHistoryError) {
      return (
        <div className="px-4 py-8 text-center">
          <p className="text-sm text-gray-400">League history is temporarily unavailable.</p>
          <button
            type="button"
            onClick={() => { void refetchLeagueHistory(); }}
            className="mt-3 min-h-11 rounded-full border border-gray-600 px-5 text-sm font-semibold text-white hover:border-accent-yellow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow"
          >
            Try again
          </button>
        </div>
      );
    }
    const events = leagueHistory ?? [];
    if (events.length === 0) {
      return (
        <p className="px-4 py-8 text-center text-sm text-gray-400">No league history recorded for this player yet.</p>
      );
    }
    return (
      <div className="px-4 py-3">
        <h3 className="mb-2 text-badge font-bold uppercase tracking-wider text-gray-500">League history</h3>
        <ol className="divide-y divide-gray-700/50 rounded-lg border border-gray-700/50 bg-primary/30">
          {events.map(event => (
            <li key={event.id} className="px-3 py-3">
              <p className="text-sm font-medium leading-snug text-gray-100">{event.text}</p>
              {event.occurred_at && (
                <time dateTime={event.occurred_at} className="mt-1 block text-xs font-medium tabular-nums text-gray-400">
                  {format(parseISO(event.occurred_at), 'MMM d, yyyy · h:mm a')}
                </time>
              )}
            </li>
          ))}
        </ol>
      </div>
    );
  };

  const renderProjectionsTab = () => (
    <>
      {isLoadingProjections && (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <Spinner size="md" color="text-accent-yellow" />
            <p className="mt-2 text-sm text-gray-400">Loading projections...</p>
          </div>
        </div>
      )}

      {projectionData && !isLoadingProjections && (
        <div className="px-4 py-3">
          {/* Summary: Projected, OPRK, Salary */}
          <div className="bg-primary/30 rounded-lg p-4 mb-4 border border-gray-700/50">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-badge text-gray-500 uppercase tracking-wider">Projected</p>
                <p className="text-xl font-bold text-accent-yellow">
                  {(() => {
                    if (!projectionData?.projected_stats) return '0.0';
                    let total = 0;
                    for (const [key, pts] of Object.entries(PROJECTION_POINT_VALUES)) {
                      const val = projectionData.projected_stats[key as keyof typeof projectionData.projected_stats];
                      if (val != null) total += val * pts;
                    }
                    return total.toFixed(1);
                  })()}
                </p>
              </div>
              <div>
                <p className="text-badge text-gray-500 uppercase tracking-wider" title={OPRK_TOOLTIP}>OPRK</p>
                <p className={`text-xl font-bold ${player.oprk != null ? getRankColor(player.oprk) : 'text-gray-400'}`}>
                  {player.oprk != null ? fmtOrdinal(player.oprk) : '0'}
                </p>
              </div>
              <div>
                <p className="text-badge text-gray-500 uppercase tracking-wider">Salary</p>
                <p className="text-xl font-bold text-accent-yellow">
                  {player.dfs_salary ? `$${player.dfs_salary.toLocaleString()}` : '-'}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3 mt-3 text-badge">
              <span className="text-gray-500" title={OPRK_TOOLTIP}>OPRK:</span>
              <span className="text-green-400 font-semibold">Good</span>
              <span className="text-yellow-400 font-semibold">Average</span>
              <span className="text-red-400 font-semibold">Tough</span>
            </div>
          </div>

          {/* Per-stat breakdown */}
          <div className="bg-primary/20 rounded-lg p-4 border border-gray-700/30">
            {!showProjectionDefinitions ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Projected Stats</h3>
                  <button
                    onClick={() => setShowProjectionDefinitions(true)}
                    className="text-badge text-accent-light-blue hover:text-accent-yellow active:text-accent-yellow transition-colors flex items-center gap-1"
                  >
                    Definitions →
                  </button>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 font-medium">
                      <th className="text-left py-1 pr-2">Stat</th>
                      <th className="text-center py-1 px-2">Proj Pts</th>
                      <th className="text-right py-1 pl-2" title={OPRK_TOOLTIP}>OPRK</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PROJECTION_STAT_ORDER.map(({ key, label }) => {
                      const projValue = projectionData.projected_stats[key as keyof typeof projectionData.projected_stats];
                      const rank = projectionData.opp_stat_ranks[key as keyof typeof projectionData.opp_stat_ranks];
                      const fp = (projValue ?? 0) * (PROJECTION_POINT_VALUES[key] ?? 0);
                      return (
                        <tr key={key} className="border-t border-gray-700/30">
                          <td className="py-1.5 pr-2">
                            <div className="flex items-center space-x-1.5">
                              <span className="text-sm">{PROJECTION_STAT_ICONS[key] || '•'}</span>
                              <span className="font-semibold text-gray-300">{label}</span>
                            </div>
                          </td>
                          <td className="py-1.5 px-2 text-center text-gray-100 font-medium">
                            {fp.toFixed(1)} <span className="text-badge text-gray-400">({(projValue ?? 0).toFixed(1)})</span>
                          </td>
                          <td className={`py-1.5 pl-2 text-right font-bold ${getRankColor(rank)}`}>
                            {rank != null ? fmtOrdinal(rank) : '0'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowProjectionDefinitions(false)}
                  className="text-badge text-accent-light-blue hover:text-accent-yellow transition-colors mb-2 flex items-center gap-1"
                >
                  ← Back to projections
                </button>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 font-medium">
                      <th className="text-left py-1 pr-2 w-[72px]">Stat</th>
                      <th className="text-left py-1 pl-2">Definition</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PROJECTION_STAT_ORDER.map(({ key }) => (
                      <tr key={key} className="border-t border-gray-700/30">
                        <td className="py-1.5 pr-2">
                          <div className="flex items-center space-x-1.5">
                            <span className="text-sm">{PROJECTION_STAT_ICONS[key] || '•'}</span>
                            <span className="font-semibold text-gray-300">
                              {PROJECTION_STAT_FULL_NAMES[key] || key}
                            </span>
                          </div>
                        </td>
                        <td className="py-1.5 pl-2 text-gray-400">
                          {PROJECTION_STAT_DEFINITIONS[key]}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}

      {!projectionData && !isLoadingProjections && (
        <p className="text-gray-500 italic text-center py-8 text-sm">
          No projection data available for this player.
        </p>
      )}
    </>
  );

  // Body scrolling is already locked while this modal is open. Keep the
  // ancestor chain free of touch handlers and persistent transforms. The
  // scroll layer is absolutely inset inside a bounded flex slot below: that
  // separates its viewport size from its asynchronously loaded content size.
  return (
    <SafeViewportOverlay
      onClose={onClose}
      closing={closing}
      portalContainer={portalContainer}
      spacing="roomy"
      zIndexClassName={isStacked ? 'z-[70]' : 'z-[60]'}
      className="flex-col items-center pointer-events-auto"
      onWheel={(e) => e.stopPropagation()}
    >
      <div
        ref={dialogRef}
        tabIndex={manageFocus ? -1 : undefined}
        role={manageFocus ? 'dialog' : undefined}
        aria-modal={manageFocus ? true : undefined}
        aria-label={manageFocus ? `${displayName} player profile` : undefined}
        className={`relative flex h-full min-h-0 max-h-full w-full max-w-3xl flex-none flex-col overflow-hidden rounded-lg border border-gray-700/50 bg-surface-card shadow-xl sm:h-[85dvh] ${closing ? 'sm:animate-scale-out' : 'sm:animate-scale-in'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-primary-dark px-4 py-3 border-b border-gray-700 flex-shrink-0">
          <div className="flex justify-between items-start gap-3">
            {/* Player Info */}
            <div className="flex-1 min-w-0">
              <h2 className="font-heading font-bold text-lg text-accent-yellow truncate" title={displayName}>
                {displayName}
              </h2>
              <div className="mt-0.5 flex min-w-0 items-center gap-2">
                <p className="min-w-0 truncate text-xs text-gray-400">
                  {teamAbbr} • {profilePosition ?? 'N/A'} • {displayCompetitionName}
                </p>
                {isMyPlayer && onDropPlayer && (
                  <button
                    type="button"
                    onClick={handleDropClick}
                    disabled={isActionLoading}
                    className="relative inline-flex min-h-8 flex-shrink-0 items-center rounded px-2 text-xs font-semibold text-red-400 transition-colors after:absolute after:-inset-y-1.5 after:-inset-x-1 after:content-[''] hover:bg-red-500/10 hover:text-red-300 active:bg-red-500/10 active:text-red-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400 disabled:opacity-50"
                    aria-label={`${dropActionLabel} ${displayName} from roster`}
                  >
                    {isActionLoading ? <Spinner size="sm" color="text-red-400" /> : dropActionLabel}
                  </button>
                )}
                {!isMyPlayer && onAddPlayer && player.current_league_availability_status === 'WAIVER' && (
                  <button
                    type="button"
                    onClick={() => handleAddClick('WAIVER')}
                    disabled={isActionLoading}
                    className="relative inline-flex min-h-8 flex-shrink-0 items-center rounded px-2 text-xs font-semibold text-green-400 transition-colors after:absolute after:-inset-y-1.5 after:-inset-x-1 after:content-[''] hover:bg-green-500/10 hover:text-green-300 active:bg-green-500/10 active:text-green-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400 disabled:opacity-50"
                    aria-label={`Claim ${displayName} on waivers`}
                  >
                    {isActionLoading ? <Spinner size="sm" color="text-green-400" /> : 'Claim'}
                  </button>
                )}
                {!isMyPlayer && onAddPlayer && player.current_league_availability_status === 'FCFS' && (
                  <button
                    type="button"
                    onClick={() => handleAddClick('FCFS')}
                    disabled={isActionLoading}
                    className="relative inline-flex min-h-8 flex-shrink-0 items-center rounded px-2 text-xs font-semibold text-green-400 transition-colors after:absolute after:-inset-y-1.5 after:-inset-x-1 after:content-[''] hover:bg-green-500/10 hover:text-green-300 active:bg-green-500/10 active:text-green-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400 disabled:opacity-50"
                    aria-label={`Add ${displayName} to roster`}
                  >
                    {isActionLoading ? <Spinner size="sm" color="text-green-400" /> : 'Add'}
                  </button>
                )}
                {!isMyPlayer && onTradePlayer && player.owner_team_id != null && (
                  <button
                    type="button"
                    onClick={handleTradeClick}
                    disabled={isActionLoading}
                    className="relative inline-flex min-h-8 flex-shrink-0 items-center rounded px-2 text-xs font-semibold text-accent-yellow transition-colors after:absolute after:-inset-y-1.5 after:-inset-x-1 after:content-[''] hover:bg-accent-yellow/10 active:bg-accent-yellow/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow disabled:opacity-50"
                    aria-label={`Trade for ${displayName}`}
                  >
                    {isActionLoading ? <Spinner size="sm" color="text-accent-yellow" /> : 'Trade'}
                  </button>
                )}
              </div>
              {showSeasonRanks && (
                <div className="mt-1 min-h-4 flex flex-wrap items-baseline gap-x-1.5 text-xs text-gray-300 tabular-nums" aria-live="polite">
                  {!seasonRankError && !isSeasonRankPending && seasonRank ? (
                    <>
                      <span className="whitespace-nowrap">
                        Overall Rank - {seasonRank.overall_rank != null ? `#${seasonRank.overall_rank}` : 'N/A'} <span aria-hidden="true">·</span>
                      </span>
                      <span className="whitespace-nowrap">
                        Position Rank - {formatPositionRank(profilePosition, seasonRank.position_rank)}
                      </span>
                    </>
                  ) : null}
                </div>
              )}
              {player.injury_status && (
                <div className="flex items-center gap-1.5 mt-1.5">
                  <InjuryBadge status={player.injury_status} details={player.injury_details} size={14} />
                  <span className="text-xs text-accent-yellow">
                    {player.injury_details || player.injury_status}
                  </span>
                </div>
              )}
            </div>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-2xl font-bold leading-none text-gray-400 transition-colors hover:bg-primary/50 hover:text-white active:bg-primary/50 active:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow"
              aria-label="Close player profile modal"
            >
              ×
            </button>
          </div>

        </div>

        {/* Tab Navigation */}
        <div ref={tabsRef} className="flex border-b border-gray-700 bg-primary/30 flex-shrink-0">
          <button
            onClick={() => { if (activeTab !== 'gameLog') trackPlayerResearch('profile_game_log', { 'Player ID': player?.id }); setActiveTab('gameLog'); }}
            className={`min-h-11 flex-1 whitespace-nowrap px-2 py-2.5 text-xs font-semibold transition-all duration-200 sm:px-4 sm:text-sm ${
              activeTab === 'gameLog'
                ? 'border border-accent-yellow text-accent-yellow bg-primary/20 rounded-md'
                : 'text-gray-400 hover:text-gray-200 hover:bg-primary/10 active:text-gray-200 active:bg-primary/10'
            }`}
          >
            Game Log
          </button>
          <button
            onClick={() => { if (activeTab !== 'seasonStats') trackPlayerResearch('profile_season_stats', { 'Player ID': player?.id }); setActiveTab('seasonStats'); }}
            className={`min-h-11 flex-1 whitespace-nowrap px-2 py-2.5 text-xs font-semibold transition-all duration-200 sm:px-4 sm:text-sm ${
              activeTab === 'seasonStats'
                ? 'border border-accent-yellow text-accent-yellow bg-primary/20 rounded-md'
                : 'text-gray-400 hover:text-gray-200 hover:bg-primary/10 active:text-gray-200 active:bg-primary/10'
            }`}
          >
            Season Stats
          </button>
          {showLeagueActivity && (
            <button
              onClick={() => { if (activeTab !== 'activity') trackPlayerResearch('profile_league_activity', { 'Player ID': player?.id }); setActiveTab('activity'); }}
              className={`min-h-11 flex-1 whitespace-nowrap px-2 py-2.5 text-xs font-semibold transition-all duration-200 sm:px-4 sm:text-sm ${
                activeTab === 'activity'
                  ? 'border border-accent-yellow text-accent-yellow bg-primary/20 rounded-md'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-primary/10 active:text-gray-200 active:bg-primary/10'
              }`}
            >
              Activity
            </button>
          )}
          {dfsContestId && (
            <button
              onClick={() => { if (activeTab !== 'projections') trackPlayerResearch('profile_projections', { 'Player ID': player?.id }); setActiveTab('projections'); }}
              className={`min-h-11 flex-1 whitespace-nowrap px-2 py-2.5 text-xs font-semibold transition-all duration-200 sm:px-4 sm:text-sm ${
                activeTab === 'projections'
                  ? 'border border-accent-yellow text-accent-yellow bg-primary/20 rounded-md'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-primary/10 active:text-gray-200 active:bg-primary/10'
              }`}
            >
              Projections
            </button>
          )}
        </div>

        {/* Season pills — govern Game Log AND Season Stats (projections are
            always current-contest and Activity is league history, so the
            pills hide on those tabs). Same visual language as the stats page
            season selector. */}
        {researchSeasons.length > 1 && activeTab !== 'projections' && activeTab !== 'activity' && (
          <div className="flex items-center justify-center gap-2 py-2 border-b border-gray-700/60 bg-primary/20 flex-shrink-0 overflow-x-auto scrollbar-mobile-hide" role="tablist" aria-label="Season">
            {researchSeasons.map(option => {
              const active = option.season_identifier === selectedStatsSeason;
              return (
                <button
                  key={option.season_identifier}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => {
                    setError(prev => prev?.replace(/\n?(Season Stats|Game Log) Error/g, '') || null);
                    setSelectedStatsSeason(option.season_identifier);
                  }}
                  className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap transition-colors border ${
                    active
                      ? 'bg-accent-yellow text-primary border-accent-yellow'
                      : 'bg-primary-dark text-gray-300 hover:text-white border-gray-700'
                  }`}
                >
                  {[
                    seasonPillLabel(option.season_identifier),
                    duplicateYearLabels.has(seasonPillLabel(option.season_identifier))
                      ? getCompetitionShortLabel(option.competition_name)
                      : '',
                  ].filter(Boolean).join(' ')}
                </button>
              );
            })}
          </div>
        )}

        {/* Tab Content. The outer flex slot has a definite height; the inset
            inner layer owns scrolling without participating in flex sizing.
            This prevents iOS WebKit from expanding the would-be scroller to
            its async table content (which leaves horizontal overflow working
            but produces no vertical scroll range). */}
        <div className="relative min-h-0 flex-1" data-player-profile-scroll-slot>
          <div
            className="absolute inset-0 overflow-x-auto overflow-y-auto overscroll-contain scrollbar-mobile-hide"
            data-player-profile-scroll
          >
            {activeTab === 'gameLog' && renderGameLogTab()}
            {activeTab === 'seasonStats' && renderSeasonStatsTab()}
            {activeTab === 'projections' && renderProjectionsTab()}
            {activeTab === 'activity' && renderActivityTab()}
          </div>
        </div>

        {showTabsCoach && (
          <CoachMark
            targetRef={tabsRef}
            // The Projections tab only exists in a DFS contest context
            // (dfsContestId gates it above) - never promise a tab that
            // isn't there (e.g. arriving from /stats).
            text={
              dfsContestId
                ? 'Toggle between Game Log, Season Stats, and Projections to research.'
                : showLeagueActivity
                  ? 'Toggle between Game Log, Season Stats, and Activity to research.'
                  : 'Toggle between Game Log and Season Stats to research.'
            }
            onDismiss={dismissTabsCoach}
          />
        )}
      </div>
    </SafeViewportOverlay>
  );
};

export default PlayerProfileModal;
